import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Apple, Play, Check, ChevronRight } from "lucide-react";
import { SITE_URL } from "@/components/SEO";

/**
 * Reusable landing-page template for SEO pages targeting long-tail queries
 * (livraison Cotonou, épicerie en ligne, devenir vendeur, etc.).
 * Each page keeps a strong H1 with keywords, 3 supporting content blocks,
 * a "how it works" list, an FAQ section, and 2 conversion CTAs (download app +
 * catalogue). Content is generated from the `config` prop.
 */
export const SeoLandingPage = ({ config }) => {
  const {
    slug,
    title,
    metaDescription,
    h1,
    intro,
    keyword,
    highlights,
    steps,
    faq,
    ctaPrimary,
    ctaSecondary,
    breadcrumb,
  } = config;
  const url = `${SITE_URL}${slug}`;
  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: breadcrumb || h1, item: url },
    ],
  };
  const faqSchema = faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((q) => ({
          "@type": "Question",
          name: q.q,
          acceptedAnswer: { "@type": "Answer", text: q.a },
        })),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbList)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FBFAF6] py-16 md:py-24 border-b border-[#EDE7D9]">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#F5A800]/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[#0E7A3B]/15 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
          <nav aria-label="Fil d'ariane" className="mb-6 flex items-center gap-1 text-sm text-[#16241B]/60">
            <Link to="/" className="hover:text-[#0E7A3B]">Accueil</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#16241B]">{breadcrumb || h1}</span>
          </nav>
          <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">
            {keyword}
          </p>
          <h1 data-testid={`seo-h1-${slug.slice(1)}`} className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-[#16241B]">
            {h1}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#16241B]/75 leading-relaxed max-w-3xl">
            {intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              data-testid={`seo-cta-app-${slug.slice(1)}`}
              href="/api/download"
              className="inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3.5 font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white transition-all"
            >
              <Apple className="h-4 w-4" /> <Play className="h-4 w-4 fill-current" />
              {ctaPrimary || "Télécharger l'app Zafara"}
            </a>
            <Link
              to="/catalogue"
              className="inline-flex items-center gap-2 rounded-full border border-[#EDE7D9] bg-white px-6 py-3.5 font-semibold hover:border-[#0E7A3B] hover:text-[#0E7A3B] transition-colors"
            >
              {ctaSecondary || "Voir le catalogue"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS — 3 blocs */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {highlights.map((h, i) => (
            <div
              key={i}
              className="rounded-3xl border border-[#EDE7D9] bg-white p-7 hover:-translate-y-0.5 hover:shadow-[0_15px_40px_rgba(14,122,59,0.06)] transition-all"
            >
              <div className="h-11 w-11 rounded-2xl bg-[#0E7A3B]/10 border border-[#0E7A3B]/20 flex items-center justify-center font-display font-bold text-[#0E7A3B]">
                0{i + 1}
              </div>
              <h2 className="mt-4 font-display text-xl md:text-2xl text-[#16241B] leading-tight tracking-tight">
                {h.title}
              </h2>
              <p className="mt-3 text-[#16241B]/70 leading-relaxed">{h.body}</p>
              {h.bullets && (
                <ul className="mt-4 space-y-1.5">
                  {h.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#16241B]/80">
                      <Check className="h-4 w-4 mt-0.5 text-[#0E7A3B] shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      {steps?.length > 0 && (
        <section className="py-16 md:py-20 bg-[#16241B] text-white">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#F5A800]">Comment ça marche</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">En 3 étapes.</h2>
            <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {steps.map((s, i) => (
                <li key={i} className="relative rounded-3xl bg-white/5 backdrop-blur-sm p-6 border border-white/10">
                  <span className="font-display font-bold text-4xl text-[#F5A800]">0{i + 1}</span>
                  <h3 className="mt-2 font-display font-semibold text-lg leading-tight">{s.title}</h3>
                  <p className="mt-2 text-sm text-white/75 leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
            <div className="mt-10">
              <a href="/api/download" className="inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3 font-display font-bold hover:bg-white transition-colors">
                <Apple className="h-4 w-4" /> Télécharger l'app maintenant
              </a>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq?.length > 0 && (
        <section className="py-16 md:py-20 bg-[#FBFAF6]">
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">Questions fréquentes</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl text-[#16241B] tracking-tight">
              Vous vous demandez…
            </h2>
            <div className="mt-10 space-y-3">
              {faq.map((q, i) => (
                <details
                  key={i}
                  data-testid={`seo-faq-${slug.slice(1)}-${i}`}
                  className="group rounded-2xl border border-[#EDE7D9] bg-white p-5 open:shadow-[0_10px_30px_rgba(14,122,59,0.06)] transition-shadow"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                    <span className="font-display font-semibold text-[#16241B] text-base md:text-lg">{q.q}</span>
                    <span className="h-6 w-6 shrink-0 rounded-full bg-[#0E7A3B]/10 flex items-center justify-center text-[#0E7A3B] group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-[#16241B]/75 leading-relaxed">{q.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#16241B] leading-tight tracking-tighter">
            Prêt à commencer ?
          </h2>
          <p className="mt-4 text-[#16241B]/70 text-lg">
            Télécharge l'application Zafara pour commander en 30 secondes.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/api/download"
              data-testid={`seo-final-cta-${slug.slice(1)}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-7 py-4 font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white hover:-translate-y-0.5 transition-all"
            >
              <Apple className="h-4 w-4" /> App Store
            </a>
            <a
              href="/api/download"
              className="inline-flex items-center gap-2 rounded-full bg-[#0E7A3B] text-white px-7 py-4 font-display font-bold shadow-[0_15px_40px_rgba(14,122,59,0.35)] hover:bg-[#F5A800] hover:text-[#16241B] hover:-translate-y-0.5 transition-all"
            >
              <Play className="h-4 w-4 fill-current" /> Google Play
            </a>
          </div>
        </div>
      </section>
    </>
  );
};
